import CalendarConfigModel from "./CalendarConfigModel.js";
import spacetime from "spacetime";
import {isNumeric} from "../helpers.js";
import RestrictionSettingsModel from "./RestrictionSettingsModel.js";
import SelectedDatesModel from "./SelectedDatesModel.js";
import MultiSelectedDatesModel from "./MultiSelectedDatesModel.js";


export default class CalendarDateModel
{
    static DATE_FORMAT = 'iso-date';
    static AVAILABILITY_STATE_NOT_AVAILABLE = 'NOT_AVAILABLE';
    static AVAILABILITY_STATE_IS_LOADING = 'IS_LOADING';
    static AVAILABILITY_STATE_HAS_MLOS = 'HAS_MLOS';
    static AVAILABILITY_STATE_CLOSED_FOR_ARRIVAL = 'CLOSED_FOR_ARRIVAL';
    static AVAILABILITY_STATE_CLOSED_FOR_DEPARTURE = 'CLOSED_FOR_DEPARTURE';
    /**
     * @var string[]
     */
    static ALL_AVAILABILITY_STATES = [
        this.AVAILABILITY_STATE_NOT_AVAILABLE,
        this.AVAILABILITY_STATE_IS_LOADING,
        this.AVAILABILITY_STATE_HAS_MLOS,
        this.AVAILABILITY_STATE_CLOSED_FOR_ARRIVAL,
        this.AVAILABILITY_STATE_CLOSED_FOR_DEPARTURE
    ];
    /**
     * @param spacetimeDate {spacetime}
     * @param calendarState {Object}
     */
    constructor(spacetimeDate, calendarState)
    {
        this.calendarState = calendarState;
        this.hotelID = calendarState.selectedDatesModel.getHotelID();
        this.uuid = calendarState.selectedDatesModel.getUUID();
        /**
         * @type {spacetime}
         */
        this.spacetimeDate = spacetimeDate;
        this.calendarConfigModel = calendarState.calendarConfigModel;
        this._isLoading = ! this.isDateClosedOut();
        this._isAvailable = false;
        this.isSelected = false;
        this.price = null;
        this.discountedPrice = null;
        this.currency = '&euro;';
        this.minLengthOfStay = null;
        this.link = '#';
        this.messages = [];
        this.isEmptyDateCell = false;
    }

    /**
     * @returns {boolean}
     */
    isHovering()
    {
        return this.calendarState.hoverDates.includes(this.getDate());
    }

    /**
     * @returns {boolean}
     */
    isLastHoverDate()
    {
        const hoverDates = this.calendarState.hoverDates;
        return hoverDates.at(hoverDates.length - 1) == this.getDate();
    }

    /**
     * @returns {boolean}
     */
    isFirstHoverDate()
    {
        return this.calendarState.hoverDates.at(0) == this.getDate();
    }

    /**
     * @param value {boolean}
     */
    setIsEmptyDateCell(value)
    {
        this.isEmptyDateCell = value;
        this._isLoading = false;
    }

    /**
     * @returns {string}
     */
    getUUID()
    {
        return this.uuid;
    }

    /**
     * @return {boolean}
     */
    isDateClosedOut() {
        if (!this.isBetweenDisplayDates()) {
            return true;
        }
        const closeoutDatetime = spacetime.now(
            this.calendarConfigModel.closeoutToday.timezone
        ).time(this.calendarConfigModel.closeoutToday.time);

        if (this.spacetimeDate.isSame(closeoutDatetime, 'date')) {
            return spacetime().isAfter(closeoutDatetime);
        }

        return this.calendarConfigModel.disableDates.includes(this.getDate());
    }


    workoutConfigRestrictions()
    {
        if (! this.isAvailable()) {
            return;
        }
        /**
         * Loop over the restrictionSettingsModels passed in and call the handler method passing
         * the CalendarDateModel and the current RestrictionSettingsModel
         * Inside the handler we can modify the CalendarDateModel to change the output of the date
         *
         * If the handler returns true it will stop all other restrictions
         */
        for (const restrictionSettingsModel of this.calendarConfigModel.restrictions) {
            if (typeof restrictionSettingsModel.handler === 'function' && restrictionSettingsModel.handler(this, restrictionSettingsModel)) {
                break;
            }
        }
    }


    /**
     * @param calendarDateModels {CalendarDateModel[]}
     */
    static setAvailabilityForBasicCalendar(calendarDateModels)
    {
        for (let date in calendarDateModels)
        {
            const calendarDateModel = calendarDateModels[date];
            calendarDateModel._isLoading = false;
            calendarDateModel.setIsAvailable(calendarDateModel.isBetweenDisplayDates());
            calendarDateModel.workoutConfigRestrictions();
        }
    }

    /**
     * @param calendarDateModel {CalendarDateModel}
     * @return {boolean}
     */
    isCalendarDateBeforeDate(calendarDateModel)
    {
        return this.spacetimeDate.isBefore(calendarDateModel.spacetimeDate);
    }

    /**
     * @param calendarDateModel {CalendarDateModel}
     * @return {boolean}
     */
    isCalendarDateAfterDate(calendarDateModel)
    {
        return this.spacetimeDate.isAfter(calendarDateModel.spacetimeDate);
    }

    /**
     * @return {boolean}
     */
    isBetweenDisplayDates()
    {
        return this.spacetimeDate.isBetween(
            spacetime(this.calendarConfigModel.minDisplayDate),
            spacetime(this.calendarConfigModel.maxDisplayDate),
            true
        );
    }


    /**
     * @param calendarDateDetails {Object}
     */
    setAvailabilityResponse(calendarDateDetails)
    {
        const isDateClosedOut = this.isDateClosedOut();
        this._isAvailable = ! isDateClosedOut;
        this._isLoading = false;
        this.price = calendarDateDetails.price;
        this.currency = calendarDateDetails.currencyEnity;
        this.discountedPrice = null;
        this.link = calendarDateDetails.href;
        this.setRestrictionsFromResponse(calendarDateDetails);
        this.workoutConfigRestrictions();
    }

    /**
     * @return {string}
     */
    getLink()
    {
        return this.link;
    }

    /**
     * @param calendarDateDetails {Object}
     */
    setRestrictionsFromResponse(calendarDateDetails)
    {
        for (let notBookableReason in Object.values(calendarDateDetails.notBookableReasons))
        {
            switch (notBookableReason.code) {
                case 'NOT_BOOKABLE_MUST_STAY_A_MINIMUM_NUMBER_OF_NIGHTS':
                    this.messages.push(CalendarDateModel.AVAILABILITY_STATE_HAS_MLOS);
                    this.minLengthOfStay = notBookableReason.amount;
                    break;
                case 'NOT_BOOKABLE_CANNOT_CHECK_IN_ON_THIS_DATE':
                    this.messages.push(CalendarDateModel.AVAILABILITY_STATE_CLOSED_FOR_ARRIVAL);
                    break;
                case 'NOT_BOOKABLE_CANNOT_CHECK_OUT_ON_THIS_DATE':
                    this.messages.push(CalendarDateModel.AVAILABILITY_STATE_CLOSED_FOR_DEPARTURE);
                    break;
                default:
                    this.setFailedCalendarDate();
                    break;
            }
        }
    }

    /**
     * If the Ajax request fails or never return a requested date we mark it as failed
     */
    setFailedCalendarDate()
    {
        this._isAvailable = false;
        this._isLoading = false;
        this.messages.push(CalendarDateModel.AVAILABILITY_STATE_NOT_AVAILABLE)
    }

    /**
     * @return {boolean}
     */
    isAvailable()
    {
        return this._isAvailable;
    }

    /**
     * @return {boolean}
     */
    isLoading()
    {
        return this._isLoading;
    }

    /**
     * @param isAvailable {boolean}
     */
    setIsAvailable(isAvailable)
    {
        this._isAvailable = isAvailable;
    }

    /**
     * @return {string}
     */
    getPrice()
    {
        return this.currency + this.price.toFixed(this.calendarConfigModel.decimalsPlaces);
    }

    /**
     * @return {boolean}
     */
    hasDiscount()
    {
        return isNumeric(this.discountedPrice);
    }

    /**
     * @return {string}
     */
    getDiscountedPrice()
    {
        return this.currency + this.discountedPrice.toFixed(this.calendarConfigModel.decimalsPlaces);
    }

    /**
     * @return {boolean}
     */
    hasPrice()
    {
        return isNumeric(this.price);
    }

    /**
     * @return {boolean}
     */
    isClickable()
    {
        return this._isAvailable;
    }

    /**
     * @returns {spacetime}
     */
    getSpacetimeDate()
    {
        return this.spacetimeDate;
    }

    getAttrs()
    {
        let attrs = {
            'tabindex': this.isEmptyDateCell ? -1 : 0,
            'class': this.getCSSClasses(),
            'key': this.getDate(),
            'role': 'button',
            'aria-description': this.getAriaText()
        }
        if (! this.calendarConfigModel.rangeMode) {
            attrs['href'] = this.link;
        }
        return attrs;
    }

    /**
     * returns true if the date is already selected by another calendar
     * @returns {boolean}
     */
    isAdditionalDate()
    {
        if (! this.calendarConfigModel.enableMultiDestinationCalendar) {
            return false;
        }

        return this.calendarState.multiSelectedDatesModel.isCalendarDateSelectedInAnotherCalendar(this);
    }

    /**
     * @return {string}
     */
    getAriaText()
    {
        let messages = [
            this.getDateInFormat('{day} {date-ordinal} {month}')
        ];
        if (this._isLoading) {
            messages.push('loading availability');
        }
        else {
            if (this.isSelected && this.isSelectedArrival()) {
                messages.push('is selected for arrival');
            }
            else if (this.isSelected && this.isSelectedDeparture()) {
                messages.push('is selected for departure');
            }
            else if (this.isSelected) {
                messages.push('is selected');
            }
            if (this.isClosedForArrival() && this.isClosedForDeparture()) {
                messages.push('is available but closed for arrival and departure');
            }
            else if (this.isClosedForArrival()) {
                messages.push('is available but closed for arrival');
            }
            else if (this.isClosedForDeparture()) {
                messages.push('is available but close for departure');
            }
            else if (this.getMinLengthOfStay() > 0) {
                messages.push(`is available but has minimum length of stay for ${this.getMinLengthOfStay()} nights`);
            }
            else if (this.isAvailable()) {
                messages.push('is available');
            }
            else {
                messages.push('has no availability');
            }
        }
        return messages.join(' ');
    }

    /**
     * @returns {string[]}
     */
    getMessages()
    {
        return this.messages;
    }

    /**
     * @return {number|null}
     */
    getMinLengthOfStay()
    {
        return this.minLengthOfStay;
    }

    /**
     * @return {boolean}
     */
    isClosedForDeparture()
    {
        return this.getMessages().includes(CalendarDateModel.AVAILABILITY_STATE_CLOSED_FOR_DEPARTURE);
    }

    /**
     * @return {boolean}
     */
    isClosedForArrival()
    {
        return this.getMessages().includes(CalendarDateModel.AVAILABILITY_STATE_CLOSED_FOR_ARRIVAL);
    }

    /**
     * @return {boolean}
     */
    isSelectableForArrival()
    {
        return this.isAvailable() && ! this.isClosedForArrival();
    }

    /**
     * @return {boolean}
     */
    isSelectableForDeparture()
    {
        return ! this.isClosedForDeparture();
    }

    /**
     * @param calendarDateModel {CalendarDateModel}
     * @return {boolean}
     */
    isSameDate(calendarDateModel)
    {
        return this.getDate() == calendarDateModel.getDate();
    }

    /**
     * @return {boolean}
     */
    isSelectedArrival()
    {
        const selectedDatesModel = this.calendarState.selectedDatesModel;
        return selectedDatesModel && selectedDatesModel.isDateArrivalDate(this.getDate());
    }
    /**
     * @return {boolean}
     */
    isSelectedDeparture()
    {
        const selectedDatesModel = this.calendarState.selectedDatesModel;
        return (
            selectedDatesModel &&
            selectedDatesModel.isDateDepartureDate(this.getDate())
        );
    }

    /**
     * @returns {object}
     */
    getCSSClasses()
    {
        const isTransferDate = (
            this.calendarConfigModel.enableMultiDestinationCalendar &&
            this.calendarState.multiSelectedDatesModel.isCalendarDateTransferDate(this)
        );
        const isHovering = (
            this.isAvailable() &&
            this.isHovering() &&
            ! this.isSelected
        );

        return {
            'calendar-date': true,
            'is-loading': this._isLoading,
            'is-available': this.isAvailable(),
            'is-disabled': ! this.isAvailable() && ! this._isLoading,
            'is-selected': this.isSelected,
            'is-additional-date': this.isAdditionalDate(),
            'is-transfer-date': isTransferDate,
            'is-arrival': this.isSelectedArrival(),
            'is-departure': this.isSelectedDeparture(),
            'is-hovering': isHovering,
            'is-hovering-last': isHovering && this.isLastHoverDate(),
            'is-hovering-first': isHovering && this.isFirstHoverDate(),
            'is-restricted': this.isClosedForArrival() || this.isClosedForDeparture(),
            'is-empty-cell': this.isEmptyDateCell,
            'is-flexible': (
                this.isAvailable() &&
                this.calendarConfigModel.highlightDate === this.getDate()
            )
        };
    }

    /**
     * @returns {string}
     */
    getDisplayDate()
    {
        return this.getDateInFormat(
            this.calendarConfigModel.getDateFormat()
        );
    }

    /**
     * @param format {string}
     * @return {string}
     */
    getDateInFormat(format)
    {
        return this.spacetimeDate.format(format);
    }
    /**
     * @returns {string}
     */
    getDate()
    {
        return this.getDateInFormat(CalendarDateModel.DATE_FORMAT);
    }

    /**
     * @param calendarDateModels {CalendarDateModel[]}
     */
    static clearSelectedDates(calendarDateModels)
    {
        for (let date in calendarDateModels)
        {
            calendarDateModels[date].isSelected = false;
        }
    }


    /**
     * @param calendarDateModels {CalendarDateModel[]}
     * @return {string[]}
     */
    static getSelectableRange(calendarDateModels)
    {
        let dates = [];

        if (calendarDateModels.length == 0) {
            return dates;
        }

        const numberOfNightsSelected = calendarDateModels.length - 1;

        for (let i = 0; i < calendarDateModels.length; ++i)
        {
            const calendarDateModel = calendarDateModels[i];

            if (calendarDateModel.isEmptyDateCell) {
                continue;
            }

            const isStartDate = i == 0;
            const isEndDate = i == calendarDateModels.length - 1;

            if (isStartDate && ! calendarDateModel.isSelectableForArrival()) {
                break;
            }
            if (isEndDate && ! calendarDateModel.isSelectableForDeparture()) {
                break;
            }
            if (calendarDateModel.getMinLengthOfStay() && numberOfNightsSelected < calendarDateModel.getMinLengthOfStay()) {
                break;
            }
            if ((! isEndDate && ! isStartDate) && calendarDateModel.isAdditionalDate()) {
                break;
            }

            dates.push(calendarDateModel.getDate());
        }

        return dates;
    }

    /**
     * @param calendarDateModels {CalendarDateModel[]}
     * @return {boolean}
     */
    static isRangeSelectable(calendarDateModels)
    {
        if (calendarDateModels.length == 0) {
            return false;
        }

        const numberOfNightsSelected = calendarDateModels.length - 1;
        let result = true;
        for (let i = 0; i < calendarDateModels.length; ++i)
        {
            const calendarDateModel = calendarDateModels[i];

            if (calendarDateModel.isEmptyDateCell) {
                continue;
            }

            const isStartDate = i == 0;
            const isEndDate = i == calendarDateModels.length - 1;

            if (isStartDate && ! calendarDateModel.isSelectableForArrival()) {
                result = false;
                break;
            }
            if (isEndDate && ! calendarDateModel.isSelectableForDeparture()) {
                result = false;
                break;
            }
            if (calendarDateModel.getMinLengthOfStay() && numberOfNightsSelected < calendarDateModel.getMinLengthOfStay()) {
                result = false;
                break;
            }
            if (calendarDateModel.isAdditionalDate()) {
                result = false;
                break;
            }

        }
        return result;
    }

    /**
     * @param calendarDateModelFrom {CalendarDateModel}
     * @param calendarDateModelTo {CalendarDateModel}
     * @return {string[]}
     */
    static getRangeDates(calendarDateModelFrom, calendarDateModelTo)
    {
        const startDate = calendarDateModelFrom.spacetimeDate.clone();
        const endDate = calendarDateModelTo.spacetimeDate;
        let dates = [];
        const days = startDate.diff(endDate, 'days');
        for (let i = 0; i <= days; ++i) {
            dates.push(
                startDate.add(i, 'day').format(this.DATE_FORMAT)
            );
        }
        return dates;
    }

    /**
     * @return {boolean}
     */
    hasMessageToDisplay()
    {
        return ! this.isLoading() && this.calendarConfigModel.getMessagesToDisplay(this).length > 0;
    }

}
